import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { environmentConfig, debugLog, shouldInitializeFirebase as shouldInit } from '../lib/environmentConfig';

interface FirebaseGuardProps {
  children?: React.ReactNode;
}

interface FirebaseConnectionState {
  isInitialized: boolean;
  isOnline: boolean;
  lastError: Error | null;
  initializationAttempts: number;
  lastSuccessfulConnection: Date | null;
}

const defaultConnectionState: FirebaseConnectionState = {
  isInitialized: false,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastError: null,
  initializationAttempts: 0,
  lastSuccessfulConnection: null
};

/**
 * Enhanced FirebaseGuard component that conditionally initializes Firebase services
 * based on the current route and environment configuration.
 */
export const FirebaseGuard: React.FC<FirebaseGuardProps> = ({ children }) => {
  const location = useLocation();
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [connectionState, setConnectionState] = useState<FirebaseConnectionState>(defaultConnectionState);

  const loadFirebaseEnvironment = (() => {
    let cached: Promise<typeof import('../lib/firebaseEnvironment')> | null = null;
    return () => {
      cached ??= import('../lib/firebaseEnvironment');
      return cached;
    };
  })();

  // Routes that require Firebase services
  const requiresFirebase = [
    '/admin',
    '/payment-success',
    '/payment-result'
  ];

  // Check if current route requires Firebase
  const routeRequiresFirebase = requiresFirebase.some(route => 
    location.pathname.startsWith(route)
  );

  // Combined check using both route and environment logic
  const shouldInitializeFirebase = routeRequiresFirebase || shouldInit();

  useEffect(() => {
    const initFirebase = async () => {
      if (shouldInitializeFirebase && !firebaseInitialized) {
        try {
          setIsLoading(true);
          setInitializationError(null);
          
          debugLog('Initializing Firebase services for route', {
            route: location.pathname,
            environment: environmentConfig.isDevelopment ? 'development' : 'production',
            useEmulators: environmentConfig.firebase.useEmulators,
            retryCount
          }, 'FirebaseGuard');

          const firebaseEnv = await loadFirebaseEnvironment();
          await firebaseEnv.ensureFirebaseInitialized();

          const latestState = firebaseEnv.getConnectionState?.();
          if (latestState) setConnectionState(latestState);

          if (firebaseEnv.isFirebaseReady()) {
            setFirebaseInitialized(true);
            debugLog('Firebase services initialized successfully', undefined, 'FirebaseGuard');
          } else {
            throw new Error('Firebase services are not ready after initialization');
          }
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error('[FirebaseGuard] Failed to initialize Firebase services:', error);
          debugLog('Firebase initialization failed', error, 'FirebaseGuard');
          setInitializationError(errorMessage);
          
          // Get detailed connection state for debugging
          try {
            const firebaseEnv = await loadFirebaseEnvironment();
            const latestState = firebaseEnv.getConnectionState?.();
            if (latestState) {
              setConnectionState(latestState);
              debugLog('Connection state after error', latestState, 'FirebaseGuard');
            }
          } catch (stateError) {
            debugLog('Failed to load Firebase environment for connection state', stateError, 'FirebaseGuard');
          }
          
        } finally {
          setIsLoading(false);
        }
      } else if (!shouldInitializeFirebase) {
        debugLog('Skipping Firebase initialization for public route', { route: location.pathname }, 'FirebaseGuard');
        setFirebaseInitialized(false);
        setInitializationError(null);
        setIsLoading(false);
        setConnectionState(defaultConnectionState);
      }
    };

    initFirebase();
  }, [location.pathname, shouldInitializeFirebase, firebaseInitialized, retryCount]);

  // Handle network state changes
  useEffect(() => {
    const handleOnline = () => {
      debugLog('Network came online, handling network change', undefined, 'FirebaseGuard');
      if (shouldInitializeFirebase) {
        loadFirebaseEnvironment()
          .then(env => env.handleNetworkChange(true))
          .catch(error => debugLog('Failed to handle online network change', error, 'FirebaseGuard'));
      }
      
      // If we had an error and network is back, try to reinitialize
      if (initializationError && shouldInitializeFirebase) {
        setRetryCount(prev => prev + 1);
      }
    };

    const handleOffline = () => {
      debugLog('Network went offline, handling network change', undefined, 'FirebaseGuard');
      if (shouldInitializeFirebase) {
        loadFirebaseEnvironment()
          .then(env => env.handleNetworkChange(false))
          .catch(error => debugLog('Failed to handle offline network change', error, 'FirebaseGuard'));
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [initializationError, shouldInitializeFirebase]);

  const handleRetry = async () => {
    try {
      setInitializationError(null);
      setIsLoading(true);
      
      debugLog('Retrying Firebase initialization...', { retryCount }, 'FirebaseGuard');

      const firebaseEnv = await loadFirebaseEnvironment();
      await firebaseEnv.reinitializeFirebase();
      setFirebaseInitialized(true);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Retry failed';
      setInitializationError(errorMessage);
      debugLog('Firebase retry failed', error, 'FirebaseGuard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceReload = () => {
    debugLog('Force reloading page', undefined, 'FirebaseGuard');
    window.location.reload();
  };

  // Show error state if Firebase initialization failed on routes that require it
  if (shouldInitializeFirebase && initializationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
            Service Initialization Error
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            Failed to initialize required services. Please refresh the page or try again later.
          </p>
          <p className="text-xs text-gray-500 text-center mb-4">
            Error: {initializationError}
          </p>
          
          {environmentConfig.enableDebugLogging && (
            <details className="mb-4 text-xs text-gray-600">
              <summary className="cursor-pointer hover:text-gray-800 font-medium">Debug Information</summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono space-y-1">
                <div>Environment: {environmentConfig.isDevelopment ? 'Development' : 'Production'}</div>
                <div>Use Emulators: {environmentConfig.firebase.useEmulators ? 'Yes' : 'No'}</div>
                <div>Project ID: {environmentConfig.firebase.projectId}</div>
                <div>Region: {environmentConfig.firebase.region}</div>
                <div>Route: {location.pathname}</div>
                <div>Initialization Attempts: {connectionState.initializationAttempts}</div>
                <div>Online: {navigator.onLine ? 'Yes' : 'No'}</div>
                <div>Last Error: {connectionState.lastError?.message || 'None'}</div>
                <div>Firebase Ready: {firebaseInitialized ? 'Yes' : 'No'}</div>
              </div>
            </details>
          )}
          
          <div className="flex space-x-3 mb-4">
            <button
              onClick={handleRetry}
              disabled={isLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Retrying...' : 'Retry Connection'}
            </button>
            
            <button
              onClick={handleForceReload}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
          
          {!navigator.onLine && (
            <div className="p-3 bg-yellow-100 border border-yellow-300 rounded">
              <p className="text-xs text-yellow-800">
                ⚠️ You appear to be offline. Please check your internet connection.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show loading state for admin routes while Firebase is initializing
  if (shouldInitializeFirebase && (isLoading || !firebaseInitialized)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {environmentConfig.firebase.useEmulators 
              ? 'Connecting to Firebase emulators...' 
              : 'Initializing services...'}
          </p>
          {environmentConfig.enableDebugLogging && (
            <p className="text-xs text-gray-500 mt-2">
              Environment: {environmentConfig.isDevelopment ? 'Development' : 'Production'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render children for all routes (Firebase will be available for admin routes)
  return <>{children}</>;
};

export default FirebaseGuard;
